// src/pages/supplier/ErpDashboard.jsx
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  TrendingUp, TrendingDown, Package, ShoppingBag, AlertTriangle,
  DollarSign, Truck, Clock, CheckCircle, ArrowRight, BarChart2,
  RefreshCw, Box, Layers,
} from 'lucide-react'
import { supplierErpApi } from '../../api/services'
import { Card, CardHeader, CardBody, StatusBadge, PageSpinner, Badge } from '../../components/common/ui'
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'

const fmt = v => `₹${Number(v || 0).toLocaleString('en-IN')}`
const fmtK = v => {
  const n = Number(v || 0)
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`
  return `₹${n.toLocaleString('en-IN')}`
}

function KpiCard({ icon: Icon, label, value, sub, trend, color = 'bg-gradient-brand' }) {
  return (
    <Card className="relative overflow-hidden">
      <CardBody className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs text-muted font-semibold uppercase tracking-wide mb-1">{label}</p>
          <p className="text-2xl font-extrabold font-display">{value}</p>
          {sub && <p className="text-xs text-muted mt-1">{sub}</p>}
          {trend !== undefined && trend !== null && (
            <div className={`flex items-center gap-1 mt-1.5 text-xs font-bold ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {trend >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {Math.abs(trend).toFixed(1)}% vs last month
            </div>
          )}
        </div>
        <div className={`w-11 h-11 rounded-2xl ${color} flex items-center justify-center flex-shrink-0`}>
          <Icon size={20} className="text-white" />
        </div>
      </CardBody>
    </Card>
  )
}

function OrderStatusPipeline({ counts }) {
  const stages = [
    { key: 'confirmed',     label: 'Confirmed',  color: 'bg-yellow-400', icon: Clock },
    { key: 'processing',    label: 'Processing',  color: 'bg-blue-400',   icon: RefreshCw },
    { key: 'ready_to_ship', label: 'Ready to Ship', color: 'bg-indigo-400', icon: Box },
    { key: 'shipped',       label: 'Shipped',     color: 'bg-purple-400', icon: Truck },
    { key: 'delivered',     label: 'Delivered',   color: 'bg-green-400',  icon: CheckCircle },
  ]
  const total = stages.reduce((s, st) => s + (counts[st.key] || 0), 0) || 1

  return (
    <div className="flex gap-1 items-end">
      {stages.map(st => {
        const c = counts[st.key] || 0
        const pct = Math.max((c / total) * 100, c > 0 ? 8 : 2)
        return (
          <div key={st.key} className="flex-1 text-center">
            <div className="text-xs font-bold mb-1">{c}</div>
            <div className={`${st.color} rounded-t-lg transition-all`} style={{ height: `${Math.max(pct * 0.6, 4)}px` }} />
            <div className="text-[10px] text-muted mt-1 truncate">{st.label}</div>
          </div>
        )
      })}
    </div>
  )
}

function StockBar({ stock, threshold }) {
  const pct = threshold > 0 ? Math.min((stock / threshold) * 100, 100) : 0
  const danger = pct <= 30
  const warn = pct <= 60
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${danger ? 'bg-red-500' : warn ? 'bg-yellow-400' : 'bg-green-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs font-bold w-8 text-right ${danger ? 'text-red-600' : warn ? 'text-yellow-600' : 'text-green-600'}`}>
        {Math.round(pct)}%
      </span>
    </div>
  )
}

export default function ErpDashboard() {
  const [tab, setTab] = useState('all')
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['supplier-erp'],
    queryFn: () => supplierErpApi.index(),
    refetchInterval: 60000,
  })

  if (isLoading) return <PageSpinner />

  const erp = data?.data?.data || {}
  const overview = erp.overview || {}
  const orders = erp.orders || {}
  const inventory = erp.inventory || {}
  const revenue = erp.revenue || []
  const payouts = erp.payouts || {}

  const orderList = orders.list || []
  const counts = orders.counts || {}
  const lowStock = inventory.low_stock || []
  const payoutSummary = payouts.summary || {}
  const recentPayouts = payouts.recent || []

  // Revenue trend
  const revThisMonth = overview.revenue_this_month || 0
  const revLastMonth = overview.revenue_last_month || 0
  const revTrend = revLastMonth > 0 ? ((revThisMonth - revLastMonth) / revLastMonth) * 100 : null

  // Filter orders by tab
  const filteredOrders = tab === 'all' ? orderList : orderList.filter(o => o.status === tab)

  return (
    <div className="space-y-6 max-w-[1200px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-display font-bold">ERP Dashboard</h1>
          <p className="text-xs text-muted mt-0.5">Unified view of your business operations</p>
        </div>
        <button onClick={() => refetch()} className="flex items-center gap-1.5 text-xs text-muted hover:text-pink transition-colors">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard
          icon={TrendingUp}
          label="Revenue"
          value={fmtK(revThisMonth)}
          sub="This month"
          trend={revTrend}
        />
        <KpiCard
          icon={ShoppingBag}
          label="Pending Orders"
          value={overview.orders_pending || 0}
          sub={`${overview.orders_to_ship || 0} ready to ship`}
          color="bg-orange-500"
        />
        <KpiCard
          icon={Package}
          label="Active Products"
          value={overview.products_active || 0}
          sub={`${overview.products_draft || 0} draft, ${overview.products_pending || 0} pending`}
          color="bg-blue-500"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Low Stock"
          value={overview.low_stock_count || 0}
          sub={`${Number(inventory.total_stock_metres || 0).toLocaleString('en-IN')}m total`}
          color={overview.low_stock_count > 0 ? 'bg-red-500' : 'bg-green-500'}
        />
        <KpiCard
          icon={DollarSign}
          label="Pending Payout"
          value={fmtK(payoutSummary.pending || 0)}
          sub={`${fmtK(payoutSummary.total_paid || 0)} total paid`}
          color="bg-purple-500"
        />
      </div>

      {/* Revenue Chart + Order Pipeline */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <span className="font-bold text-sm">Revenue (Last 30 Days)</span>
            <span className="text-xs text-muted">{revenue.length} data points</span>
          </CardHeader>
          <CardBody>
            {revenue.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={revenue}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF3D7F" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#FF3D7F" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => fmtK(v)} width={50} />
                  <Tooltip formatter={v => fmt(v)} labelFormatter={l => `Date: ${l}`} />
                  <Area type="monotone" dataKey="total" stroke="#FF3D7F" strokeWidth={2} fill="url(#revGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted text-center py-12">No revenue data yet.</p>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <span className="font-bold text-sm">Order Pipeline</span>
            <span className="text-xs text-muted">{overview.orders_total || 0} total</span>
          </CardHeader>
          <CardBody>
            <OrderStatusPipeline counts={counts} />
            <div className="mt-4 pt-4 border-t border-border space-y-2">
              {counts.confirmed > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <Clock size={12} className="text-yellow-500" />
                  <span className="font-bold text-yellow-700">{counts.confirmed} awaiting confirmation</span>
                </div>
              )}
              {counts.ready_to_ship > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <Truck size={12} className="text-indigo-500" />
                  <span className="font-bold text-indigo-700">{counts.ready_to_ship} ready to ship</span>
                </div>
              )}
              <Link to="/supplier/orders" className="flex items-center gap-1 text-xs text-pink font-bold hover:underline mt-2">
                View all orders <ArrowRight size={12} />
              </Link>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Orders Table + Low Stock */}
      <div className="grid lg:grid-cols-5 gap-4">
        {/* Recent Orders */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <span className="font-bold text-sm">Recent Orders</span>
            <div className="flex gap-1">
              {[
                { key: 'all', label: 'All' },
                { key: 'confirmed', label: 'New' },
                { key: 'processing', label: 'Processing' },
                { key: 'ready_to_ship', label: 'Ready' },
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all ${
                    tab === t.key ? 'bg-pink text-white border-pink' : 'border-border text-muted hover:border-pink'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {filteredOrders.length === 0 ? (
              <p className="text-sm text-muted text-center py-8">No orders to show.</p>
            ) : (
              <div className="divide-y divide-border">
                {filteredOrders.map(o => (
                  <div key={o.id} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-surface/50 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm">{o.order_number}</span>
                        <StatusBadge status={o.status} />
                        {o.sla_breached && <Badge variant="red">SLA Breached</Badge>}
                      </div>
                      <p className="text-xs text-muted mt-0.5">
                        {o.buyer} &middot; {o.items_count} item(s) &middot; {new Date(o.created_at).toLocaleDateString('en-IN')}
                      </p>
                      {o.sla_due && !o.sla_breached && (
                        <p className="text-[10px] text-yellow-600 mt-0.5">
                          SLA due: {new Date(o.sla_due).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-sm">{fmt(o.total)}</p>
                      <Link to={`/supplier/orders/${o.id}`} className="text-xs text-pink hover:underline">Manage</Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Low Stock Alerts */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <span className="font-bold text-sm flex items-center gap-1.5">
              {overview.low_stock_count > 0 && <AlertTriangle size={13} className="text-red-500" />}
              Inventory Alerts
            </span>
            <span className="text-xs text-muted">{fmt(inventory.total_stock_metres || 0).replace('₹', '')}m total</span>
          </CardHeader>
          <CardBody className="p-0">
            {lowStock.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle size={24} className="text-green-400 mx-auto mb-2" />
                <p className="text-sm text-muted">All stock levels healthy</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {lowStock.map(item => (
                  <div key={item.variant_id} className="px-5 py-3">
                    <div className="flex items-start justify-between mb-1.5">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold truncate">{item.product_name}</p>
                        <p className="text-[10px] text-muted">{item.sku} &middot; {item.color}</p>
                      </div>
                      <span className={`text-xs font-bold flex-shrink-0 ${item.percent <= 30 ? 'text-red-600' : 'text-yellow-600'}`}>
                        {item.stock_metres}m / {item.threshold}m
                      </span>
                    </div>
                    <StockBar stock={item.stock_metres} threshold={item.threshold} />
                  </div>
                ))}
              </div>
            )}
            <div className="px-5 py-3 border-t border-border">
              <Link to="/supplier/products" className="flex items-center gap-1 text-xs text-pink font-bold hover:underline">
                Manage inventory <ArrowRight size={12} />
              </Link>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Payouts Section */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Payout Stats */}
        <Card>
          <CardHeader><span className="font-bold text-sm">Payout Summary</span></CardHeader>
          <CardBody className="space-y-4">
            <div>
              <p className="text-xs text-muted font-semibold uppercase tracking-wide">Pending Payout</p>
              <p className="text-2xl font-extrabold font-display text-purple-600">{fmt(payoutSummary.pending)}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] text-muted uppercase">Paid This Month</p>
                <p className="text-sm font-bold">{fmtK(payoutSummary.paid_this_month)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted uppercase">Total Commission</p>
                <p className="text-sm font-bold text-orange-600">{fmtK(payoutSummary.total_commission)}</p>
              </div>
            </div>
            <Link to="/supplier/payouts" className="flex items-center gap-1 text-xs text-pink font-bold hover:underline">
              View payouts <ArrowRight size={12} />
            </Link>
          </CardBody>
        </Card>

        {/* Orders per day chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <span className="font-bold text-sm">Daily Orders (Last 30 Days)</span>
          </CardHeader>
          <CardBody>
            {revenue.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={revenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d?.slice(8)} />
                  <YAxis tick={{ fontSize: 10 }} width={30} allowDecimals={false} />
                  <Tooltip formatter={(v, name) => [v, name === 'count' ? 'Orders' : name]} labelFormatter={l => `Date: ${l}`} />
                  <Bar dataKey="count" fill="#FF8500" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted text-center py-12">No order data yet.</p>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Recent Payouts */}
      {recentPayouts.length > 0 && (
        <Card>
          <CardHeader><span className="font-bold text-sm">Recent Payouts</span></CardHeader>
          <CardBody className="p-0">
            <div className="divide-y divide-border">
              {recentPayouts.map(p => (
                <div key={p.id} className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-sm">{fmt(p.amount)}</p>
                    <p className="text-xs text-muted">{new Date(p.date).toLocaleDateString('en-IN')} &middot; {p.mode}</p>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ['Add Product',    '/supplier/products/new',  '+ New',  'bg-pink text-white'],
          ['View Analytics', '/supplier/analytics',     <BarChart2 size={20} />, 'border border-border hover:border-pink'],
          ['Manage Warehouse', '/supplier/warehouse',   <Layers size={20} />, 'border border-border hover:border-pink'],
          ['Edit Storefront', '/supplier/storefront',   <Package size={20} />, 'border border-border hover:border-pink'],
        ].map(([label, to, icon, cls]) => (
          <Link key={label} to={to} className={`${cls} rounded-xl p-4 text-center font-bold text-sm transition-colors`}>
            <span className="text-xl block mb-1">{icon}</span>{label}
          </Link>
        ))}
      </div>
    </div>
  )
}
